/**
 * 侧边栏 —— 六组导航（03-UIUX.md §4.1）+ 底部身份区。
 *
 * 折叠/抽屉行为全部由 CSS 断点承担（styles/app.css §8.1），组件不做窗口宽度判断：
 * 用 JS 测宽度会在缩放、分屏、字号放大时产生与 CSS 不一致的第二套真相。
 */
import { NavLink } from 'react-router-dom';
import { Icon } from '../icons/Icon';
import { NAV_GROUPS } from '../app/navigation';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';

export interface SidebarProps {
  readonly open: boolean;
  readonly onNavigate: () => void;
  readonly theme: 'light' | 'dark';
  readonly onToggleTheme: () => void;
}

export function Sidebar({ open, onNavigate, theme, onToggleTheme }: SidebarProps) {
  const { currentUser } = useAuth();
  const nextThemeLabel = theme === 'dark' ? '切换到浅色主题' : '切换到深色主题';

  const identityLabel = (() => {
    if (currentUser === undefined) return '加载中…';
    if (currentUser === null) return '未登录';
    return `${currentUser.name} · ${currentUser.role}`;
  })();
  const identityTitle = (() => {
    if (currentUser === undefined) return '正在探测登录会话';
    if (currentUser === null) return '当前没有活跃的管理员会话';
    return `当前登录：${currentUser.name}（${currentUser.email}），角色 ${currentUser.role}`;
  })();

  return (
    <nav
      className="sidebar"
      data-open={open ? 'true' : 'false'}
      aria-label="主导航"
      id="app-sidebar"
    >
      <div className="sidebar__brand">
        <span className="sidebar__brand-mark">
          <Icon name="shield-check" size={ICON_SIZE.control} />
        </span>
        <span className="sidebar__brand-text">LicenseHarbor</span>
      </div>

      <div className="sidebar__scroll">
        {NAV_GROUPS.map((group) => (
          <div className="sidebar__group" key={group.title}>
            <h2 className="sidebar__group-title">{group.title}</h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {group.items.map((item) => (
                <li key={item.path + '-' + item.label}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    className="sidebar__link"
                    onClick={onNavigate}
                    title={item.implemented ? item.label : `${item.label}（本迭代未实现）`}
                  >
                    <Icon name={item.icon} size={ICON_SIZE.control} />
                    <span className="sidebar__link-text">{item.label}</span>
                    {item.implemented ? null : (
                      <span className="sidebar__pending">未实现</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="sidebar__footer">
        <span className="sidebar__identity" title={identityTitle}>
          {identityLabel}
        </span>
        <button
          type="button"
          className="btn btn--quiet btn--icon"
          onClick={onToggleTheme}
          aria-label={nextThemeLabel}
          title={nextThemeLabel}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={ICON_SIZE.inline} />
        </button>
        <button
          type="button"
          className="btn btn--quiet btn--icon"
          disabled
          aria-label="退出登录（登录流程未接入，暂不可用）"
          title="退出登录（登录流程未接入，暂不可用）"
        >
          <Icon name="log-out" size={ICON_SIZE.inline} />
        </button>
      </div>
    </nav>
  );
}
